/**
 * VIVA HUB unified login — split-screen layout
 */

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useSchedulerAuth } from '../contexts/SchedulerAuthContext'
import { getAccessibleApps } from '../constants/hub-apps'
import HubAuthLayout from '../components/hub/HubAuthLayout'
import TurnstileWidget from '../components/TurnstileWidget'
import { TURNSTILE_ACTIONS } from '../constants/turnstile'
import { useTurnstile } from '../hooks/useTurnstile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const REMEMBER_EMAIL_KEY = 'viva_hub_remember_email'

function readRememberedEmail() {
  if (typeof window === 'undefined') return ''
  return localStorage.getItem(REMEMBER_EMAIL_KEY) || ''
}

export default function HubLogin() {
  const [email, setEmail] = useState(readRememberedEmail)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(() => Boolean(readRememberedEmail()))
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const turnstile = useTurnstile()
  const { login, user, loading: authLoading } = useSchedulerAuth()
  const navigate = useNavigate()
  const emailRef = useRef(null)

  useEffect(() => {
    if (user && !authLoading) {
      navigate('/hub', { replace: true })
    }
  }, [user, authLoading, navigate])

  useLayoutEffect(() => {
    if (authLoading || user) return

    const focusEmailField = () => {
      emailRef.current?.focus({ preventScroll: true })
    }

    focusEmailField()
    const timerId = window.setTimeout(focusEmailField, 0)
    return () => window.clearTimeout(timerId)
  }, [authLoading, user])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const turnstileError = turnstile.getRequiredError()
    if (turnstileError) {
      setError(turnstileError)
      return
    }

    setLoading(true)

    try {
      const result = await login(email, password, turnstile.token)
      if (result.success) {
        if (rememberMe) {
          localStorage.setItem(REMEMBER_EMAIL_KEY, email)
        } else {
          localStorage.removeItem(REMEMBER_EMAIL_KEY)
        }

        await new Promise((resolve) => setTimeout(resolve, 200))
        const apps = getAccessibleApps(result.user)
        if (apps.length === 0) {
          setError('Your account does not have access to any VIVA HUB apps.')
          return
        }
        navigate('/hub', { replace: true })
      } else {
        setError(result.error || 'Login failed')
        turnstile.reset()
      }
    } catch (err) {
      setError(err.message || 'An error occurred')
      turnstile.reset()
    } finally {
      setLoading(false)
    }
  }

  const canSubmit = !loading && turnstile.canSubmit

  return (
    <HubAuthLayout>
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        {error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3" role="alert">
            <p className="text-sm font-medium text-destructive">{error}</p>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="email" className="sr-only">
            Email address
          </Label>
          <Input
            ref={emailRef}
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="h-11 text-base"
          />
        </div>

        <div className="hub-login-password-only relative">
          <Label htmlFor="password" className="sr-only">
            Password
          </Label>
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 pr-11 text-base"
            placeholder="Password"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute inset-y-0 right-0 h-full w-10 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword((visible) => !visible)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            aria-controls="password"
          >
            {showPassword ? <EyeOff /> : <Eye />}
          </Button>
        </div>

        <div className="flex items-center justify-between gap-4">
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
            <span className="text-sm text-muted-foreground">Remember for 30 days</span>
          </label>
          <Link
            to="/scheduler/forgot-password"
            className="text-sm font-medium text-primary hover:opacity-80"
          >
            Forgot password
          </Link>
        </div>

        <div
          className="hub-login-turnstile-host"
          role="group"
          aria-labelledby="hub-login-turnstile-label"
        >
          <p
            id="hub-login-turnstile-label"
            className="mb-3 text-center text-sm font-medium text-muted-foreground"
          >
            Security verification
          </p>
          <TurnstileWidget
            action={TURNSTILE_ACTIONS.HUB_LOGIN}
            resetSignal={turnstile.resetSignal}
            onTokenChange={turnstile.setToken}
            onError={turnstile.clearToken}
          />
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          className="h-11 w-full bg-primary-darkGreen text-base text-white hover:bg-primary-darkGreen/90"
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </HubAuthLayout>
  )
}
